import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { queryHealth } from "@/lib/analytics.functions";
import { queryApmHealth } from "@/lib/apm/apm-health.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Radio, CreditCard, Rocket, Eye } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/health")({
  head: () => ({ meta: [{ title: "Health — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: HealthPage,
});

function StatusCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  tone: "ok" | "warn" | "err";
  hint?: string;
}) {
  const color = tone === "ok" ? "text-[var(--green)]" : tone === "warn" ? "text-[var(--gold)]" : "text-red-600";
  const Icon = tone === "ok" ? CheckCircle2 : AlertTriangle;
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">{label}</p>
        <Icon size={16} className={color} />
      </div>
      <p className={`mt-1 font-display text-2xl font-extrabold ${color}`}>{value}</p>
      {hint && <p className="mt-1 text-[11px] text-[var(--gray-ink)]">{hint}</p>}
    </div>
  );
}

function HealthPage() {
  const fn = useServerFn(queryHealth);
  const q = useQuery({ queryKey: ["a-health"], queryFn: () => fn(), refetchInterval: 30_000 });
  const apmFn = useServerFn(queryApmHealth);
  const apm = useQuery({ queryKey: ["a-health-apm"], queryFn: () => apmFn(), refetchInterval: 30_000 });

  const errors = q.data?.errorsLastHour ?? 0;
  const webhooks = q.data?.pendingWebhooks?.length ?? 0;
  const stuck = q.data?.stuckWebhooks?.length ?? 0;
  const inconsistent = q.data?.inconsistentSubs?.length ?? 0;
  const stale = q.data?.staleProjectsCount ?? 0;
  const lastEvents = q.data?.lastEvents ?? [];
  const staleTelemetry = lastEvents.filter((e) => e.alert).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <PageHeader title="Health Check" description="Erros, webhooks, assinaturas, ativação e telemetria." />
        <button
          onClick={() => q.refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-[var(--gray-soft)]"
        >
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatusCard label="Erros / 1h" value={errors} tone={errors === 0 ? "ok" : errors < 5 ? "warn" : "err"} />
        <StatusCard
          label="Webhooks pendentes"
          value={webhooks}
          tone={webhooks === 0 ? "ok" : webhooks < 3 ? "warn" : "err"}
        />
        <StatusCard
          label="Webhooks parados > 15m"
          value={stuck}
          tone={stuck === 0 ? "ok" : "err"}
          hint="Requer investigação"
        />
        <StatusCard
          label="Assinaturas inconsistentes"
          value={inconsistent}
          tone={inconsistent === 0 ? "ok" : "warn"}
          hint="Ativas sem order_id"
        />
        <StatusCard
          label="Projetos sem 1ª tarefa"
          value={stale}
          tone={stale === 0 ? "ok" : stale < 5 ? "warn" : "err"}
          hint="Ativos há > 48h"
        />
        <StatusCard
          label="Telemetria em atraso"
          value={staleTelemetry}
          tone={staleTelemetry === 0 ? "ok" : "warn"}
          hint="Eventos críticos"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-black/[0.06] bg-white p-4">
          <p className="mb-3 flex items-center gap-2 font-display text-sm font-bold">
            <Radio size={14} /> Última chegada por evento
          </p>
          <ul className="divide-y divide-black/5 text-sm">
            {lastEvents.map((e) => (
              <li key={e.name} className="flex items-center justify-between py-2">
                <span className="font-mono text-xs">{e.name}</span>
                <span className={`text-xs ${e.alert ? "font-semibold text-red-600" : "text-[var(--gray-ink)]"}`}>
                  {e.last
                    ? `${new Date(e.last).toLocaleString("pt-BR")} (${e.staleHours}h)`
                    : "sem registro"}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-black/[0.06] bg-white p-4">
          <p className="mb-3 flex items-center gap-2 font-display text-sm font-bold">
            <CreditCard size={14} /> Assinaturas inconsistentes
          </p>
          {inconsistent === 0 ? (
            <p className="text-sm text-[var(--gray-ink)]">Nenhuma inconsistência detectada.</p>
          ) : (
            <ul className="max-h-64 divide-y divide-black/5 overflow-auto text-sm">
              {(q.data?.inconsistentSubs ?? []).map((s: any) => (
                <li key={s.id} className="flex justify-between py-2">
                  <span className="font-mono text-[11px] text-[var(--gray-ink)]">{s.user_id.slice(0, 8)}…</span>
                  <span className="text-xs">
                    granted {new Date(s.granted_at).toLocaleDateString("pt-BR")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-black/[0.06] bg-white p-4">
        <p className="mb-3 flex items-center gap-2 font-display text-sm font-bold">
          <Activity size={14} /> Últimos erros (24h)
        </p>
        {(q.data?.recentErrors ?? []).length === 0 ? (
          <p className="text-sm text-[var(--gray-ink)]">Nenhum erro registrado nas últimas 24h.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
                <th className="pb-2">Quando</th>
                <th className="pb-2">Ação</th>
                <th className="pb-2">Entidade</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.recentErrors ?? []).map((e: any) => (
                <tr key={e.id} className="border-t border-black/5">
                  <td className="py-2 text-xs text-[var(--gray-ink)]">
                    {new Date(e.created_at).toLocaleString("pt-BR")}
                  </td>
                  <td className="py-2 font-semibold">{e.action}</td>
                  <td className="py-2">{e.entity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-black/[0.06] bg-white p-4">
          <p className="mb-3 font-display text-sm font-bold">Webhooks não processados</p>
          {(q.data?.pendingWebhooks ?? []).length === 0 ? (
            <p className="text-sm text-[var(--gray-ink)]">Nenhum webhook pendente.</p>
          ) : (
            <ul className="divide-y divide-black/5 text-sm">
              {(q.data?.pendingWebhooks ?? []).map((w: any) => (
                <li key={w.id} className="flex justify-between py-2">
                  <span>
                    <span className="font-semibold">{w.source}</span> — {w.event_type ?? "?"}
                  </span>
                  <span className="text-xs text-[var(--gray-ink)]">
                    {new Date(w.created_at).toLocaleString("pt-BR")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-black/[0.06] bg-white p-4">
          <p className="mb-3 flex items-center gap-2 font-display text-sm font-bold">
            <Rocket size={14} /> Ativação de projetos
          </p>
          <p className="text-sm text-[var(--gray-ink)]">
            {stale === 0
              ? "Todos os projetos ativos têm ao menos uma tarefa iniciada."
              : `${stale} projeto(s) criado(s) há mais de 48h sem tarefa concluída. Considere um empurrão manual (e-mail, mensagem).`}
          </p>
        </div>
      </div>

      {/* SDE.4 · Onda 4.2 — Painel APM */}
      <div className="rounded-xl border border-black/[0.06] bg-white p-4">
        <p className="mb-3 flex items-center gap-2 font-display text-sm font-bold">
          <Eye size={14} /> Observabilidade (APM)
        </p>
        {apm.isLoading ? (
          <p className="text-sm text-[var(--gray-ink)]">Carregando…</p>
        ) : apm.data ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <StatusCard
              label="Adapter"
              value={apm.data.adapter ?? "no-op"}
              tone={apm.data.adapter ? "ok" : "warn"}
              hint={apm.data.adapter ? "Provedor ativo" : "Sem provedor registrado"}
            />
            <StatusCard
              label="Client"
              value={apm.data.flags.client ? "ON" : "OFF"}
              tone={apm.data.flags.client ? "ok" : "warn"}
            />
            <StatusCard
              label="Server"
              value={apm.data.flags.server ? "ON" : "OFF"}
              tone={apm.data.flags.server ? "ok" : "warn"}
            />
            <StatusCard
              label="Routes /public"
              value={apm.data.flags.routes_public ? "ON" : "OFF"}
              tone={apm.data.flags.routes_public ? "ok" : "warn"}
            />
            <StatusCard
              label="Kill-switch"
              value={apm.data.killSwitchEnv ? "ATIVO" : "livre"}
              tone={apm.data.killSwitchEnv ? "err" : "ok"}
              hint={`Retenção ${apm.data.retentionDays}d · DPA ${apm.data.dpaSigned ? "OK" : "pendente"}`}
            />
          </div>
        ) : (
          <p className="text-sm text-red-600">Falha ao carregar estado do APM.</p>
        )}
      </div>
    </div>
  );
}
